import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import StatusPill from "../components/StatusPill";
import {
  Briefcase, AlertCircle, DollarSign, Clock, ClipboardCheck, Receipt, ArrowRight,
} from "lucide-react";

const STAT_CARDS = [
  { key: "jobs_today", label: "Jobs Today", icon: Briefcase, color: "text-blue-600", bg: "bg-blue-50" },
  { key: "new_leads", label: "New Leads", icon: AlertCircle, color: "text-slate-600", bg: "bg-slate-100" },
  { key: "scheduled", label: "Scheduled", icon: Clock, color: "text-blue-600", bg: "bg-blue-50" },
  { key: "in_progress", label: "In Progress", icon: ClipboardCheck, color: "text-amber-600", bg: "bg-amber-50" },
  { key: "completed", label: "Completed", icon: ClipboardCheck, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "unpaid_invoices", label: "Unpaid Invoices", icon: Receipt, color: "text-violet-600", bg: "bg-violet-50" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="p-8 lg:p-10" data-testid="dashboard-page">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Overview</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Control Room</h1>
          <p className="text-sm text-slate-500 mt-1">Today's operations at a glance.</p>
        </div>
        <Link to="/booking/new" data-testid="dashboard-new-booking"
          className="hidden sm:inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm px-4 py-2 rounded-md transition-colors">
          New Booking <ArrowRight className="w-4 h-4" />
        </Link>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {STAT_CARDS.map((c) => {
          const Icon = c.icon;
          const value = stats?.[c.key] ?? 0;
          return (
            <div key={c.key} data-testid={`stat-${c.key}`}
              className="bg-white border border-slate-200 rounded-md p-4 card-hover">
              <div className={`w-9 h-9 rounded-md ${c.bg} ${c.color} flex items-center justify-center mb-3`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="text-3xl font-display font-semibold tracking-tight">{value}</div>
              <div className="text-xs text-slate-500 mt-1">{c.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center gap-2 text-emerald-700 mb-2">
            <DollarSign className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Week revenue</span>
          </div>
          <div className="text-5xl font-display font-semibold tracking-tight" data-testid="stat-week-revenue">
            ${stats?.week_revenue?.toLocaleString() ?? 0}
          </div>
          <div className="text-xs text-slate-500 mt-2">From paid invoices in the last 7 days</div>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="font-display font-medium">Upcoming jobs</div>
            <Link to="/jobs" className="text-xs text-blue-600 hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {!stats?.upcoming?.length && (
              <div className="p-6 text-sm text-slate-500" data-testid="upcoming-empty">No upcoming jobs.</div>
            )}
            {stats?.upcoming?.map((j) => (
              <Link to={`/jobs/${j.id}`} key={j.id}
                className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors">
                <div>
                  <div className="text-sm font-medium text-slate-900">{j.customer_name}</div>
                  <div className="text-xs text-slate-500">
                    {j.date} · {j.time} · {j.service_type.replace("_", " ")}
                  </div>
                </div>
                <StatusPill status={j.status} />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
